// 7-day forecast — premium construction weather widget.
// Used on Job detail and Dispatch. Presentation only; data/API untouched.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { weatherVisual, weatherStyles } from "@/lib/weather";
import { CloudSun, Droplets, Wind } from "lucide-react";

const BAR = {
  green: "bg-emerald-500/80",
  amber: "bg-amber-400/90",
  red: "bg-red-500/90",
  unknown: "bg-zinc-400/40",
};
const LABEL_CLR = {
  green: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  red: "text-red-600 dark:text-red-400",
  unknown: "text-zinc-500 dark:text-zinc-400",
};

function dayParts(iso) {
  const d = new Date(`${iso}T00:00:00`);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  return {
    isToday,
    weekday: d.toLocaleDateString("en-NZ", { weekday: "short" }),
    daynum: d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" }),
  };
}

export default function WeatherStrip({ lat, lng, address, highlightDate, onDayClick, title = "7-day forecast on site" }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!lat || !lng) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    setForecast(null);
    api
      .get("/weather/forecast", { params: { lat, lng } })
      .then((r) => mounted && setForecast(r.data))
      .catch(() => mounted && setError(true))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  const Header = (
    <div className="flex items-center gap-1.5 mb-2">
      <CloudSun className="w-3.5 h-3.5 text-[#FF5F15]" />
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF5F15]">{title}</span>
    </div>
  );

  if (!lat || !lng) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/5 p-3 text-xs text-zinc-500" data-testid="weather-strip-no-loc">
        Add a site address to see the 7-day forecast for this job.
      </div>
    );
  }
  if (loading) {
    return (
      <div data-testid="weather-strip-loading">
        {Header}
        <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className={`h-[150px] rounded-lg bg-zinc-100 dark:bg-white/5 animate-pulse ${i > 3 ? "hidden md:block" : ""}`} />
          ))}
        </div>
      </div>
    );
  }
  if (error || !forecast) {
    return (
      <div className="rounded-lg border border-zinc-200 dark:border-white/10 p-3 text-xs text-zinc-500" data-testid="weather-strip-error">
        Forecast unavailable. Try again later.
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="weather-strip">
      {Header}
      <div className="flex md:grid md:grid-cols-7 gap-2 overflow-x-auto md:overflow-visible no-scrollbar pb-1 md:pb-0">
        {forecast.days.map((d) => {
          const { Icon, cls } = weatherVisual(d);
          const S = weatherStyles(d.severity);
          const { isToday, weekday, daynum } = dayParts(d.date);
          const highlight = d.date === highlightDate;
          const clickable = !!onDayClick;
          const Tag = clickable ? "button" : "div";
          const wet = d.severity === "red";
          const rainMm = d.rain_mm || 0;
          return (
            <Tag
              key={d.date}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onDayClick(d.date) : undefined}
              className={[
                "group relative flex flex-col min-h-[150px] min-w-[126px] shrink-0 md:min-w-0 md:shrink rounded-lg border p-3 pb-3.5 text-left overflow-hidden",
                "transition-[transform,border-color,box-shadow] duration-150 hover:-translate-y-[1px] hover:shadow-lg",
                wet
                  ? "bg-red-50/60 border-red-200 dark:bg-gradient-to-b dark:from-[#1E1216] dark:to-[#140D10] dark:border-red-500/25 dark:hover:border-red-500/40"
                  : "bg-white border-zinc-200 hover:border-zinc-300 dark:bg-gradient-to-b dark:from-[#131A26] dark:to-[#0E141E] dark:border-white/10 dark:hover:border-white/25",
                isToday ? "border-[#FF5F15]/60 dark:border-[#FF5F15]/45 dark:from-[#18202E]" : "",
                highlight ? "ring-2 ring-[#FF5F15] ring-offset-1 dark:ring-offset-0" : "",
                clickable ? "cursor-pointer" : "",
              ].join(" ")}
              data-testid={`weather-day-${d.date}`}
            >
              {/* Day header */}
              <div className="flex items-baseline justify-between gap-1">
                <span className={`text-[10px] font-bold uppercase tracking-widest ${isToday ? "text-[#FF5F15]" : "text-zinc-500 dark:text-zinc-400"}`}>
                  {isToday ? "Today" : weekday}
                </span>
                <span className="text-[9px] font-mono text-zinc-400 dark:text-zinc-500">{daynum}</span>
              </div>

              {/* Icon */}
              <Icon className={`w-9 h-9 my-2 ${cls}`} strokeWidth={1.7} />

              {/* Temps */}
              <div className="flex items-baseline gap-1.5">
                <span className="font-display font-black text-xl leading-none text-zinc-900 dark:text-white">
                  {d.temp_max != null ? `${Math.round(d.temp_max)}°` : "—"}
                </span>
                <span className="text-xs font-semibold text-zinc-400 dark:text-zinc-500">
                  {d.temp_min != null ? `${Math.round(d.temp_min)}°` : ""}
                </span>
              </div>

              {/* Condition label */}
              <div className={`text-[9px] font-bold uppercase tracking-wider mt-1 leading-tight ${LABEL_CLR[d.severity] || LABEL_CLR.unknown}`}>
                {d.label}
              </div>

              {/* Details */}
              <div className="mt-auto pt-2 space-y-1">
                <div className={`flex items-center gap-1 text-[10px] font-mono ${wet ? "text-red-600 dark:text-red-400 font-bold" : rainMm > 0 || (d.rain_prob || 0) >= 20 ? "text-zinc-600 dark:text-zinc-300" : "text-zinc-400 dark:text-zinc-600"}`}>
                  <Droplets className="w-3 h-3 shrink-0" />
                  {rainMm > 0 ? `${rainMm.toFixed(1)}mm · ` : ""}{d.rain_prob || 0}%
                </div>
                <div className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
                  <Wind className="w-3 h-3 shrink-0" />
                  {(d.wind_kmh || 0).toFixed(0)} km/h
                </div>
              </div>

              {/* Severity bottom bar */}
              <span className={`absolute inset-x-2.5 bottom-0 h-[3px] rounded-t-full ${BAR[d.severity] || BAR.unknown}`} aria-hidden="true" />
            </Tag>
          );
        })}
      </div>
      <div className="text-[9px] text-zinc-400 dark:text-zinc-500 italic mt-1.5">{forecast.attribution}</div>
    </div>
  );
}
